/** Textos e conversões da tela do calendário forense (sem React, testáveis à parte). */
import type {
  Abrangencia,
  Justica,
  Recorrencia,
  RegraForense,
  RegraForenseInput,
  TipoRegra,
} from '@/api/calendarioForense';

export const JUSTICAS: { value: Justica; label: string }[] = [
  { value: 'estadual', label: 'Estadual' },
  { value: 'federal', label: 'Federal' },
  { value: 'trabalho', label: 'Trabalho' },
  { value: 'eleitoral', label: 'Eleitoral' },
  { value: 'militar_uniao', label: 'Militar da União' },
  { value: 'militar_estadual', label: 'Militar estadual' },
  { value: 'superior', label: 'Tribunais superiores' },
];

export const UFS = [
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
];

export const TIPOS: { value: TipoRegra; label: string; dica: string }[] = [
  { value: 'feriado', label: 'Feriado', dica: 'Sem expediente: o dia não é útil e prorroga o vencimento.' },
  { value: 'recesso', label: 'Recesso', dica: 'Há expediente e a publicação vale, mas o prazo não corre (CPC 220).' },
  { value: 'suspensao', label: 'Suspensão de prazos', dica: 'Há expediente, mas o prazo não corre (CPC 221).' },
];

export const RECORRENCIAS: { value: Recorrencia; label: string }[] = [
  { value: 'anual', label: 'Todo ano no mesmo dia' },
  { value: 'periodo_anual', label: 'Todo ano no mesmo período' },
  { value: 'pascoa', label: 'Pela Páscoa (Carnaval, Semana Santa, Corpus Christi)' },
  { value: 'unica', label: 'Data única (ato de um ano)' },
];

export const ABRANGENCIAS: { value: Abrangencia; label: string }[] = [
  { value: 'nacional', label: 'Todo o país' },
  { value: 'estadual', label: 'Um estado' },
  { value: 'municipal', label: 'Um município' },
  { value: 'tribunal', label: 'Um tribunal (1º e 2º grau)' },
  { value: 'tribunal_2grau', label: 'Só o 2º grau de um tribunal' },
  { value: 'unidade', label: 'Uma unidade (vara, subseção, comarca)' },
];

/** `2026-08-11` → `11/08/2026`, sem passar por Date (fuso). */
export function fmtData(iso: string): string {
  const [a, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
}

function diaMes(iso: string): string {
  return fmtData(iso).slice(0, 5);
}

export function descreverQuando(r: Pick<RegraForense, 'recorrencia' | 'data' | 'data_fim' | 'offset_pascoa'>): string {
  switch (r.recorrencia) {
    case 'anual':
      return `${diaMes(r.data)}, todo ano`;
    case 'periodo_anual':
      return `${diaMes(r.data)} a ${r.data_fim ? diaMes(r.data_fim) : '?'}, todo ano`;
    case 'pascoa': {
      const n = r.offset_pascoa ?? 0;
      if (n === 0) return 'Domingo de Páscoa';
      const dias = Math.abs(n) === 1 ? 'dia' : 'dias';
      return n < 0 ? `${-n} ${dias} antes da Páscoa` : `${n} ${dias} depois da Páscoa`;
    }
    case 'unica':
      return r.data_fim && r.data_fim !== r.data ? `${fmtData(r.data)} a ${fmtData(r.data_fim)}` : fmtData(r.data);
  }
}

export function descreverOnde(
  r: Pick<RegraForense, 'abrangencia' | 'uf' | 'municipio_nome' | 'municipio_ibge' | 'tribunal' | 'orgao'>,
): string {
  switch (r.abrangencia) {
    case 'nacional':
      return 'Todo o país';
    case 'estadual':
      return `Estado: ${r.uf ?? '?'}`;
    case 'municipal':
      return `${r.municipio_nome ?? r.municipio_ibge ?? '?'}${r.uf ? ` (${r.uf})` : ''}`;
    case 'tribunal':
      return r.tribunal ?? '?';
    case 'tribunal_2grau':
      return `${r.tribunal ?? '?'} — só no 2º grau`;
    case 'unidade':
      return `${r.tribunal ?? '?'} — ${r.orgao ?? '?'}`;
  }
}

export function descreverJusticas(justicas: Justica[] | null): string | null {
  if (!justicas?.length) return null;
  return justicas.map((j) => JUSTICAS.find((x) => x.value === j)?.label ?? j).join(', ');
}

export function rotuloTipo(tipo: TipoRegra): string {
  return TIPOS.find((t) => t.value === tipo)?.label ?? tipo;
}

function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function filtrarPorTexto(regras: RegraForense[], busca: string): RegraForense[] {
  const termo = normalizar(busca.trim());
  if (!termo) return regras;
  return regras.filter((r) =>
    normalizar([r.nome, r.ato_normativo, descreverOnde(r), r.observacao ?? ''].join(' ')).includes(termo),
  );
}

export function regraVazia(): RegraForenseInput {
  return {
    nome: '',
    tipo: 'feriado',
    recorrencia: 'anual',
    data: '',
    data_fim: null,
    offset_pascoa: null,
    excecoes: null,
    abrangencia: 'nacional',
    justicas: null,
    uf: null,
    municipio_ibge: null,
    municipio_nome: null,
    tribunal: null,
    orgao: null,
    ato_normativo: '',
    observacao: null,
  };
}

/** Só os campos da regra — a sugestão traz outros (advogado, situação) que não vão ao backend. */
export function regraParaInput(r: RegraForenseInput): RegraForenseInput {
  return {
    nome: r.nome,
    tipo: r.tipo,
    recorrencia: r.recorrencia,
    data: r.data,
    data_fim: r.data_fim,
    offset_pascoa: r.offset_pascoa,
    excecoes: r.excecoes,
    abrangencia: r.abrangencia,
    justicas: r.justicas,
    uf: r.uf,
    municipio_ibge: r.municipio_ibge,
    municipio_nome: r.municipio_nome,
    tribunal: r.tribunal,
    orgao: r.orgao,
    ato_normativo: r.ato_normativo ?? '',
    observacao: r.observacao,
  };
}

/**
 * Dias em relação à Páscoa como o admin digita ou cola da dica (com o "−" tipográfico).
 * `null` quando não é um inteiro de −100 a 100.
 */
export function lerOffsetPascoa(texto: string): number | null {
  const limpo = texto.replace(/\u2212/g, '-').replace(/\s/g, '');
  if (!/^[-+]?\d{1,3}$/.test(limpo)) return null;
  const n = Number(limpo);
  return n >= -100 && n <= 100 ? n : null;
}

/** Hoje em Brasília, ISO — o "hoje" do calendário forense. */
export function hojeIso(agora: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(agora);
}

export function somarDias(iso: string, dias: number): string {
  const [a, m, d] = iso.split('-').map(Number);
  const data = new Date(Date.UTC(a, m - 1, d + dias));
  return data.toISOString().slice(0, 10);
}
